import {
  type FieldPath,
  type FieldValues,
  useFormContext,
} from 'react-hook-form';

import { Label } from '@/modules/ui/components/label/Label';
import {
  RadioGroup,
  RadioGroupItem,
} from '@/modules/ui/components/radio-group/RadioGroup';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './Forms';

interface RadioOption {
  value: string;
  label: string;
}

interface FormRadioGroupProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TName;
  label: string;
  description?: string;
  disabled?: boolean;
  required?: boolean;
  options: RadioOption[];
}

export function FormRadioGroup<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  description,
  disabled,
  required,
  options,
}: FormRadioGroupProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel required={required}>{label}</FormLabel>
          <FormControl>
            <RadioGroup
              {...field}
              disabled={disabled}
              onValueChange={field.onChange}
            >
              {options.map((option) => {
                const optionKey = `${name}-${option.value}`;

                return (
                  <div key={option.value} className="flex items-center gap-2">
                    <RadioGroupItem
                      id={optionKey}
                      value={option.value}
                      aria-label={option.label}
                    />
                    <Label
                      htmlFor={optionKey}
                      className="cursor-pointer font-normal"
                    >
                      {option.label}
                    </Label>
                  </div>
                );
              })}
            </RadioGroup>
          </FormControl>

          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
